<template>
  <div>
    <NavigationBar
      :actions="actions"
      :backRoute="backRoute"
      :tabs="hideNavigation ? [] : tabs"
      :activeTab="activeTab"
      @active-tab-updated="activeTab = $event"
      :hideTabs="hideNavigation"
      :showBackBtn="!hideNavigation"
      :isPreview="!hideNavigation"
    >
      <template v-slot:title>
        <div v-if="selectedRecordId && personnelRecord">
          <small class="mb-0 pb-0 grey--text font-weight-medium caption"
            >Personaldaten</small
          >
          <small class="mb-0 pb-0 font-weight-medium caption hidden-md-and-up">
            {{ personnelRecord.firstName }}
            {{ personnelRecord.lastName }}</small
          >
          <div class="mt-n1 pt-0 pb-2 title text-h6 font-weight-medium">
            <span class="hidden-sm-and-down">
              {{ personnelRecord.firstName }}
              {{ personnelRecord.lastName }}
            </span>

            <person-switcher-selector-menu
              v-model="currentSelection"
              :items="personnelRecords"
              :enableNavigation="!hideNavigation"
              @selectionChange="handleSelectionChange"
            >
            </person-switcher-selector-menu>
          </div>
        </div>
        <div v-else-if="hideNavigation">
          <small class="mb-0 pb-0 grey--text font-weight-medium caption"
            >Personalakte</small
          >
          <div class="mt-n1 pt-0 pb-2 title text-h6 font-weight-medium">
            Keine Auswahl
            <person-switcher-selector-menu
              v-model="currentSelection"
              :items="personnelRecords"
              :enableNavigation="false"
              @selectionChange="handleSelectionChange"
            >
            </person-switcher-selector-menu>
          </div>
        </div>
        <div v-else>
          <small class="mb-0 pb-0 grey--text font-weight-medium caption"
            >Personaldaten</small
          >
          <div class="mt-n1 pt-0 pb-2 title text-h6 font-weight-medium">
            Personalakte wird geladen...
          </div>
        </div>
      </template>
    </NavigationBar>

    <!-- Split view mode: Use imported components directly -->
    <div v-if="hideNavigation" class="split-view-content">
      <!-- Tabs for split view when record is selected -->
      <div v-if="selectedRecordId && personnelRecord">
        <v-tabs v-model="activeTabIndex" show-arrows class="flex-shrink-0">
          <v-tab v-for="tab in tabs" :key="tab.id" :disabled="tab.disabled">
            {{ tab.name }}
          </v-tab>
        </v-tabs>
        <v-divider></v-divider>
      </div>

      <div v-if="selectedRecordId && personnelRecord" class="pa-4">
        <!-- Debug Tab - Raw Data for Testing -->
        <div v-if="currentTabRoute === 'debug'">
          <h3 class="text-h6 mb-4">Raw Personnel Record Data (Debug)</h3>
          <v-card outlined class="pa-4">
            <pre class="debug-data">{{
              JSON.stringify(personnelRecord, null, 2)
            }}</pre>
          </v-card>
        </div>

        <!-- General Tab -->
        <personnel-record-details-tab-general
          v-else-if="currentTabRoute === 'general'"
          :personnelRecord="personnelRecord"
          :propContainerWidth="propContainerWidth"
        />

        <!-- Contact Tab -->
        <personnel-record-details-tab-contact
          v-else-if="currentTabRoute === 'contact'"
          :personnelRecord="personnelRecord"
          :propContainerWidth="propContainerWidth"
          :propPersonnelRecordId="selectedRecordId"
        />

        <!-- Chronicle Tab -->
        <personnel-record-details-tab-chronicle
          v-else-if="currentTabRoute === 'chronicle'"
          :personnelRecord="personnelRecord"
        />

        <!-- Attendances Tab -->
        <personnel-record-details-tab-attendances
          v-else-if="currentTabRoute === 'attendances'"
          :personnelRecord="personnelRecord"
          :propContainerWidth="propContainerWidth"
          :propPersonnelRecordId="selectedRecordId"
        />

        <!-- Deadlines Tab -->
        <personnel-record-details-tab-deadlines
          v-else-if="currentTabRoute === 'deadlines'"
          :personnelRecord="personnelRecord"
          :propPersonnelRecordId="selectedRecordId"
        />

        <!-- Certificates Tab -->
        <personnel-record-details-tab-certificates
          v-else-if="currentTabRoute === 'certificates'"
          :personnelRecord="personnelRecord"
          :propContainerWidth="propContainerWidth"
          :propPersonnelRecordId="selectedRecordId"
        />

        <!-- Emergency Contacts Tab -->
        <personnel-record-details-tab-emergency-contacts
          v-else-if="currentTabRoute === 'emergency-contacts'"
          :personnelRecord="personnelRecord"
          :propPersonnelRecordId="selectedRecordId"
        />

        <!-- Accounting Tab -->
        <personnel-record-details-tab-accounting
          v-else-if="currentTabRoute === 'accounting'"
          :personnelRecord="personnelRecord"
        />

        <!-- Youth Group Tab -->
        <personnel-record-details-tab-youth-group
          v-else-if="currentTabRoute === 'youth-group'"
          :personnelRecord="personnelRecord"
        />
      </div>

      <!-- Empty State for split view -->
      <div v-else class="text-center pa-12">
        <v-icon size="64" color="grey lighten-2">mdi-account-search</v-icon>
        <p class="grey--text mt-4 mb-0">Wählen Sie eine Personalakte aus</p>
      </div>
    </div>

    <!-- Single view mode: Use router-view -->
    <router-view v-else :personnelRecord="personnelRecord" />
  </div>
</template>

<script>
import { db } from "@/firebase";
import NavigationBar from "@/components/_systemwide/NavigationBar.vue";
import PersonSwitcherSelectorMenu from "@/components/_system/helpers/PersonSwitcherSelectorMenu.vue";
// Import tab components for split view
import PersonnelRecordDetailsTabGeneral from "@/views/member-management/personnel-records/PersonnelRecordDetailsTabGeneral.vue";
import PersonnelRecordDetailsTabContact from "@/views/member-management/personnel-records/PersonnelRecordDetailsTabContact.vue";
import PersonnelRecordDetailsTabChronicle from "@/views/member-management/personnel-records/PersonnelRecordDetailsTabChronicle.vue";
import PersonnelRecordDetailsTabAttendances from "@/views/member-management/personnel-records/PersonnelRecordDetailsTabAttendances.vue";
import PersonnelRecordDetailsTabDeadlines from "@/views/member-management/personnel-records/PersonnelRecordDetailsTabDeadlines.vue";
import PersonnelRecordDetailsTabCertificates from "@/views/member-management/personnel-records/PersonnelRecordDetailsTabCertificates.vue";
import PersonnelRecordDetailsTabEmergencyContacts from "@/views/member-management/personnel-records/PersonnelRecordDetailsTabEmergencyContacts.vue";
import PersonnelRecordDetailsTabAccounting from "@/views/member-management/personnel-records/PersonnelRecordDetailsTabAccounting.vue";
import PersonnelRecordDetailsTabYouthGroup from "@/views/member-management/personnel-records/PersonnelRecordDetailsTabYouthGroup.vue";

import {
  MEMBER_MANAGEMENT_PERSONNEL_RECORDS_update,
  MEMBER_MANAGEMENT_PERSONNEL_RECORDS_GENERAL_update,
  MEMBER_MANAGEMENT_PERSONNEL_RECORDS_CONTACT_update,
  MEMBER_MANAGEMENT_PERSONNEL_RECORDS_CHRONICLE_update,
  MEMBER_MANAGEMENT_PERSONNEL_RECORDS_CERTIFICATES_update,
  // MEMBER_MANAGEMENT_PERSONNEL_RECORDS_PHYSICALS_update,
  MEMBER_MANAGEMENT_PERSONNEL_RECORDS_EMERGENCY_CONTACTS_update,
  MEMBER_MANAGEMENT_PERSONNEL_RECORDS_ACCOUNTING_update,
  MEMBER_MANAGEMENT_PERSONNEL_RECORDS_ATTENDANCE_update,
  MEMBER_MANAGEMENT_PERSONNEL_RECORDS_YOUTH_GROUP_update,
} from "@/data/permission-types.js";

export default {
  name: "member-management-personnel-records-details",
  components: {
    NavigationBar,
    PersonSwitcherSelectorMenu,
    // Tab components for split view
    PersonnelRecordDetailsTabGeneral,
    PersonnelRecordDetailsTabContact,
    PersonnelRecordDetailsTabChronicle,
    PersonnelRecordDetailsTabAttendances,
    PersonnelRecordDetailsTabDeadlines,
    PersonnelRecordDetailsTabCertificates,
    PersonnelRecordDetailsTabEmergencyContacts,
    PersonnelRecordDetailsTabAccounting,
    PersonnelRecordDetailsTabYouthGroup,
  },
  props: {
    // When used as a component, these props can be passed
    propPersonnelRecord: {
      type: Object,
      default: null,
    },
    propPersonnelRecords: {
      type: Array,
      default: () => [],
    },
    propOrganizationId: {
      type: String,
      default: null,
    },
    propSelectedRecordId: {
      type: String,
      default: null,
    },
    // When true, hides the NavigationBar (for use in panes)
    hideNavigation: {
      type: Boolean,
      default: false,
    },
    // Container width for responsive breakpoints in split pane contexts
    propContainerWidth: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      activeTab: `general`,
      activeTabIndex: 0, // For split view tabs
      personnelRecord: {
        id: null,
        salutation: null,
        letterSalutation: null,
        title: null,
        firstName: null,
        lastName: null,
        birthday: null,
        birthdate: null,
        photoUrl: null,

        memberSince: null,
        memberUntil: null,
        dutyPosition: {
          badgeUrl: {
            size70x70: null,
            size125x125: null,
          },
          title: null,
          since: null,
        },
        rank: {
          badgeUrl: {
            size70x70: null,
            size125x125: null,
          },
          title: null,
          since: null,
        },
        status: null,
        division: null,
      },
    };
  },
  computed: {
    actions() {
      const baseActions = [
        {
          title: "Bearbeiten",
          permissions: `${MEMBER_MANAGEMENT_PERSONNEL_RECORDS_update}`,
          icon: "mdi-pencil",
          actionStyle: "textBtn",
          function: this.editItem,
        },
      ];

      // Only add clipboard button for single view mode (not split view)
      if (!this.hideNavigation) {
        baseActions.push({ actionStyle: "clipboardBtn" });
      }

      return baseActions;
    },
    tabs() {
      const allTabs = [
        {
          id: 1,
          name: "Stammdaten",
          route: `general`,
          permission: `${MEMBER_MANAGEMENT_PERSONNEL_RECORDS_GENERAL_update}`,
        },
        {
          id: 2,
          name: "Kontaktdaten",
          route: `contact`,
          permission: `${MEMBER_MANAGEMENT_PERSONNEL_RECORDS_CONTACT_update}`,
          disabled: false,
        },
        {
          id: 3,
          name: "Chronik",
          route: `chronicle`,
          permission: `${MEMBER_MANAGEMENT_PERSONNEL_RECORDS_CHRONICLE_update}`,
          disabled: true,
        },
        {
          id: 4,
          name: "Anwesenheit",
          route: `attendances`,
          permission: `${MEMBER_MANAGEMENT_PERSONNEL_RECORDS_ATTENDANCE_update}`,
        },
        {
          id: 5,
          name: "Fristen",
          route: `deadlines`,
          permission: `${MEMBER_MANAGEMENT_PERSONNEL_RECORDS_CHRONICLE_update}`,
        },
        {
          id: 6,
          name: "Fertigkeiten",
          route: `certificates`,
          permission: `${MEMBER_MANAGEMENT_PERSONNEL_RECORDS_CERTIFICATES_update}`,
        },
        // {
        //   id: 7,
        //   name: "Untersuchungen",
        //   route: `physicals`,
        //   disabled: true,
        //   permission: `${MEMBER_MANAGEMENT_PERSONNEL_RECORDS_PHYSICALS_update}`,
        // },
        {
          id: 8,
          name: "Notfallkontakte",
          route: `emergency-contacts`,
          permission: `${MEMBER_MANAGEMENT_PERSONNEL_RECORDS_EMERGENCY_CONTACTS_update}`,
          disabled: false,
        },
        { id: 11, name: "Inventar", route: `inventory`, disabled: true },
        {
          id: 12,
          name: "Abrechnung",
          route: `accounting`,
          disabled: true,
          permission: `${MEMBER_MANAGEMENT_PERSONNEL_RECORDS_ACCOUNTING_update}`,
        },
        {
          id: 13,
          name: "Jugendfeuerwehr",
          route: `youth-group`,
          collectionId: "personnelRecords_youthGroup",
          disabled: true,
          permission: `${MEMBER_MANAGEMENT_PERSONNEL_RECORDS_YOUTH_GROUP_update}`,
        },
        { id: 99, name: "Änderungshistorie", route: ``, disabled: true },
      ];

      // Add debug tab for split view mode and support users only
      if (this.hideNavigation && this.checkIfSupport()) {
        allTabs.push({
          id: 100,
          name: "Debug (Raw Data)",
          route: `debug`,
          permission: "", // Always available for support users
        });
      }

      return allTabs.filter((tab) => {
        return this.checkPermission(tab.permission);
      });
    },
    currentTabRoute() {
      // For split view mode, get the route based on activeTabIndex
      if (this.hideNavigation) {
        return this.tabs[this.activeTabIndex]?.route || "general";
      }
      return this.activeTab;
    },
    backRoute() {
      return this.$route.path.includes ===
        "/personnel-records/" + this.$route.params.itemId
        ? "member-management-personnel-records"
        : "";
    },
    personnelRecords() {
      return this.propPersonnelRecords.length > 0
        ? this.propPersonnelRecords
        : this.$store.state.memberManagement.personnelRecords;
    },
    organizationId() {
      return this.propOrganizationId || this.$route.params.organizationId;
    },
    selectedRecordId() {
      return this.propSelectedRecordId || this.$route.params.itemId;
    },
    currentSelection: {
      get() {
        return this.selectedRecordId;
      },
      set(value) {
        this.handleSelectionChange(value);
      },
    },
  },
  watch: {
    $route: "initialize",
    propPersonnelRecord: {
      immediate: true,
      handler(newRecord) {
        if (newRecord) {
          this.personnelRecord = newRecord;
        }
      },
    },
    selectedRecordId: {
      immediate: true,
      handler(newId) {
        if (newId && !this.propPersonnelRecord) {
          this.getPersonnelRecord();
        }
      },
    },
  },
  created() {
    // If propPersonnelRecord is provided, use it directly
    if (this.propPersonnelRecord) {
      this.personnelRecord = this.propPersonnelRecord;
    } else {
      this.initialize();
    }
  },
  methods: {
    initialize() {
      this.getPersonnelRecord();
    },
    getPersonnelRecord() {
      db.collection("organizations")
        .doc(this.organizationId)
        .collection("personnelRecords_general")
        .doc(this.selectedRecordId)
        .get()
        .then((doc) => {
          if (doc.exists) {
            this.personnelRecord = doc.data();
            this.dataIsLoading = false;
          } else {
            // doc.data() will be undefined in this case
            console.log("No such document!");
          }
        })
        .catch(function (error) {
          console.log("Error getting document:", error);
          return false;
        });
    },
    editItem() {
      this.$router.push({
        name: `member-management-personnel-records-edit-${
          this.activeTab || "general"
        }`,
        params: {
          organizationId: this.organizationId,
          itemId: this.selectedRecordId,
        },
      });
    },
    checkPermission(item) {
      if (item == "" || item == undefined) {
        return true;
      } else {
        return this.$store.getters["organization/checkPermissionByID"](item);
      }
    },
    checkIfSupport() {
      return this.$store.getters["organization/checkIfSupport"];
    },
    handleSelectionChange(selectedRecordId) {
      // Emit the selection change for parent components (like PersonnelRecordPane)
      this.$emit("selection-changed", selectedRecordId);

      // If not in hidden navigation mode (i.e., not in a split pane), handle route change
      if (!this.hideNavigation) {
        this.$router.push({
          name: this.$route.name,
          params: {
            ...this.$route.params,
            itemId: selectedRecordId,
          },
        });
      }
    },
  },
};
</script>

<style>
.border-bottom {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.debug-data {
  font-size: 12px;
  line-height: 1.4;
  background-color: #f5f5f5;
  overflow: auto;
  max-height: 600px;
}

.split-view-content {
  height: 100%;
  display: flex;
  flex-direction: column;
}
</style>
